import { dateTime } from "@lumberset/takeoff-ui";
import { Container, EmptyState, SectionHeader, Table } from "@lumberset/ui";
import { ScrollText } from "@medusajs/icons";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { auditSentence, auditTargetLabel } from "../../components/audit";
import { Crumbs } from "../../components/crumbs";
import { auditLogQuery } from "../../lib/queries";

export const Route = createFileRoute("/_app/audit-log")({
  loader: ({ context: { queryClient } }) => queryClient.ensureQueryData(auditLogQuery),
  component: AuditLog,
});

/** One line of JSON, or nothing when there is nothing to say. */
function compactJson(data: Record<string, unknown> | null): string {
  if (!data || Object.keys(data).length === 0) return "";
  return JSON.stringify(data);
}

function AuditLog() {
  const { data } = useSuspenseQuery(auditLogQuery);

  return (
    <>
      <Crumbs trail={[{ label: "Audit log" }]} />
      <Container className="divide-y p-0">
        <SectionHeader
          level="h1"
          title="Audit log"
          description="Who changed what across the platform, newest first. The last 200 entries."
        />
        {data.entries.length === 0 ? (
          <EmptyState
            icon={<ScrollText />}
            title="Nothing recorded yet"
            description="Changes to Billing Plans, subscriptions, Staff, settings and the Ledger land here."
          />
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Time</Table.HeaderCell>
                <Table.HeaderCell>Who</Table.HeaderCell>
                <Table.HeaderCell>Action</Table.HeaderCell>
                <Table.HeaderCell>About</Table.HeaderCell>
                <Table.HeaderCell>Details</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data.entries.map((entry) => {
                const json = compactJson(entry.data);
                return (
                  <Table.Row key={entry.id}>
                    <Table.Cell className="whitespace-nowrap">
                      {dateTime(entry.createdAt)}
                    </Table.Cell>
                    <Table.Cell className="whitespace-nowrap">
                      {entry.actorName ? (
                        <span className="txt-compact-small-plus text-ui-fg-base">
                          {entry.actorName}
                        </span>
                      ) : (
                        <span className="text-ui-fg-muted">System</span>
                      )}
                      {entry.impersonatorId ? (
                        <div className="txt-compact-xsmall text-ui-fg-muted">
                          by Admin {entry.impersonatorName ?? "(removed)"}
                        </div>
                      ) : null}
                    </Table.Cell>
                    <Table.Cell className="min-w-[240px]">
                      <span className="txt-compact-small text-ui-fg-base">
                        {auditSentence(entry.action, entry.data)}
                      </span>
                      <div className="txt-compact-xsmall font-mono text-ui-fg-muted">
                        {entry.action}
                      </div>
                    </Table.Cell>
                    <Table.Cell className="whitespace-nowrap">
                      {entry.targetType ? (
                        <>
                          <span>
                            {auditTargetLabel(entry.targetType)}
                            {typeof entry.data?.name === "string" ? `: ${entry.data.name}` : ""}
                          </span>
                          {entry.targetId ? (
                            <div className="txt-compact-xsmall font-mono text-ui-fg-muted">
                              {entry.targetId}
                            </div>
                          ) : null}
                        </>
                      ) : (
                        "-"
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {json ? (
                        <code
                          className="txt-compact-xsmall block max-w-[420px] truncate font-mono text-ui-fg-subtle"
                          title={json}
                        >
                          {json}
                        </code>
                      ) : (
                        "-"
                      )}
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        )}
      </Container>
    </>
  );
}
